/**
 * The recent searches, kept in the browser.
 *
 * `SearchViewModel` persists the `SearchResultItem` the reader chose - deduped by id, newest
 * first, capped at ten - in plain `UserDefaults`, and draws its recent rows from that rather
 * than asking the server again. This is the same store on the web: `localStorage` rather than
 * `UserDefaults`, under the same key, and nothing in it ever reaches the server. A list of who
 * somebody looked up is theirs, and the server has no reason to hold one.
 *
 * What is kept per entry is the row the server already rendered, not the fields it was rendered
 * from. A recent row has to look exactly like a result row - the same squircle avatar, the same
 * two-line name, the same flag and chevron, every one of them a component-library fragment and
 * inline SVG - and rebuilding that in TypeScript would be a second copy of
 * `fragment/search-result/table` to keep in step. Keeping the markup means there is only ever
 * one version of a row. The cost is that an entry stored before that fragment changed is drawn
 * the old way until it is chosen again: a cosmetic difference, in a list of at most ten rows,
 * that heals itself as they are used.
 */

/** The same key `SearchViewModel` uses, so the two stores are recognisably the same one. */
const KEY = "com.getweplay.search.recent_searches";

/** `SearchViewModel.maxRecentSearches`. */
const LIMIT = 10;

/**
 * What never comes back out of the store, whatever is in it.
 *
 * The store is same-origin and this page is its only writer, so the markup in it is already
 * Thymeleaf-escaped and the only person who can put anything else there is the reader, in their
 * own browser, to their own browser. That is nobody's attack on anybody. But `<img onerror>`
 * fires from a parsed document whether or not somebody meant it to, and a stored row is the one
 * string on this page that outlives the page that wrote it, so stripping the handlers and the
 * elements that fetch or run anything costs a dozen lines and removes the question.
 */
const FORBIDDEN_ELEMENTS = "embed, iframe, link, meta, object, script, style";

/** The schemes that are code or a payload rather than somewhere to go. */
const FORBIDDEN_SCHEME = /^\s*(data|javascript|vbscript):/i;

export interface RecentSearch {
  /** The result's own id, which is what an entry is deduped and removed by. */
  readonly id: string;

  /** The result row's `innerHTML`, as the server rendered it. */
  readonly row: string;

  /** Where the row goes, which is the row's own `href`. */
  readonly url: string;
}

function isRecentSearch(value: unknown): value is RecentSearch {
  if (typeof value !== "object" || value === null) {
    return false;
  }

  const candidate = value as Partial<Record<keyof RecentSearch, unknown>>;

  return (
    typeof candidate.id === "string" &&
    candidate.id !== "" &&
    typeof candidate.row === "string" &&
    typeof candidate.url === "string" &&
    candidate.url !== ""
  );
}

/*
 * Every read and write is guarded, because `localStorage` is not always there to be used: a
 * browser told to block site data throws on the property itself, and a private window has
 * historically thrown on `setItem` the moment anything was put in it. Neither is a reason for the
 * search screen to stop working, so a store that cannot be reached is an empty one.
 */
function read(): string | null {
  try {
    return window.localStorage.getItem(KEY);
  } catch {
    return null;
  }
}

function write(entries: readonly RecentSearch[]): void {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(entries));
  } catch {
    /* Nothing to do and nothing to say: the list the caller was handed is still right for the
           rest of this page's life, it just will not survive into the next one. */
  }
}

/**
 * The stored searches, newest first.
 *
 * Anything that is not a list of entries of the shape above is discarded whole rather than
 * repaired. That covers a store written by an older version of this file as well as one somebody
 * has edited by hand, and in both cases ten rows are worth less than the certainty that what
 * comes back out is what this file put in.
 */
export function load(): RecentSearch[] {
  const stored = read();
  if (stored === null) {
    return [];
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(stored);
  } catch {
    return [];
  }

  if (!Array.isArray(parsed) || !parsed.every(isRecentSearch)) {
    return [];
  }

  return parsed.slice(0, LIMIT);
}

/**
 * Puts an entry at the front, whether or not it was in the list already.
 *
 * `SearchViewModel.selectResult`: choosing a recent search again moves it up rather than adding
 * a second copy of it, which is why one call covers both.
 */
export function promote(entry: RecentSearch): RecentSearch[] {
  const kept = load().filter((stored) => stored.id !== entry.id);
  const entries = [entry, ...kept].slice(0, LIMIT);
  write(entries);

  return entries;
}

/** `SearchViewModel.deleteRecentSearch`, by id rather than by offset: a list is not an IndexSet. */
export function remove(id: string): RecentSearch[] {
  const entries = load().filter((stored) => stored.id !== id);
  write(entries);

  return entries;
}

/** `SearchViewModel.clearRecentSearches`. */
export function clear(): RecentSearch[] {
  write([]);

  return [];
}

/**
 * A stored row as nodes this page can adopt.
 *
 * `DOMParser` rather than assigning the string to an element and tidying up afterwards: the
 * document it builds has no browsing context, so nothing in it loads, runs or fires while it is
 * being looked over. The stripping happens there, before any of it is anywhere a handler could
 * fire from.
 */
export function rowFragment(row: string): DocumentFragment {
  const parsed = new DOMParser().parseFromString(row, "text/html");

  parsed.body
    .querySelectorAll(FORBIDDEN_ELEMENTS)
    .forEach((element) => element.remove());

  parsed.body.querySelectorAll("*").forEach((element) => {
    element.getAttributeNames().forEach((name) => {
      const lowered = name.toLowerCase();

      /* `on*` is every inline handler there is. A location is left alone unless its scheme is
               code: the avatar's `src` and the row's own `href` are two of the three things a
               row is made of. */
      if (
        lowered.startsWith("on") ||
        (["href", "src", "srcset"].includes(lowered) &&
          FORBIDDEN_SCHEME.test(element.getAttribute(name) ?? ""))
      ) {
        element.removeAttribute(name);
      }
    });
  });

  const fragment = document.createDocumentFragment();
  Array.from(parsed.body.childNodes).forEach((node) =>
    fragment.append(document.importNode(node, true)),
  );

  return fragment;
}
